/**
 * Family Member Edit Screen
 *
 * Full-page editor for family member details.
 * Replaces the edit modal with a dedicated screen.
 */

import { useCallback, useState, useMemo } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import Animated, { FadeIn } from 'react-native-reanimated';

import { Colors, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getHeadingFont, getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useFamilyMember } from '@/hooks/use-family-member';

import { useMemberCalendarMapping } from '@/hooks/use-member-calendar-mapping';
import { FamilyMemberForm } from '@/components/family/FamilyMemberForm';
import { FamilyMemberPhotoEditor } from '@/components/family/FamilyMemberPhotoEditor';
import { FamilyMemberColorPicker } from '@/components/family/FamilyMemberColorPicker';
import type { UpdateFamilyMemberInput } from '@/types/family-member';
import { getContrastTextColor } from '@/lib/utils/color-contrast';

export default function FamilyMemberEditScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  // Load member data
  const { member, isLoading, error, updateMember, deleteMember } = useFamilyMember(id ?? null);
  const { linkedCalendarIds } = useMemberCalendarMapping(id ?? null);

  const [isSaving, setIsSaving] = useState(false);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleSave = useCallback(async (input: UpdateFamilyMemberInput) => {
    try {
      setIsSaving(true);
      await updateMember(input);
      Alert.alert('Saved', 'Changes saved.');
    } catch (err) {
      console.error('Failed to save member:', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [updateMember]);

  const handleDelete = useCallback(async () => {
    try {
      setIsSaving(true);
      const success = await deleteMember();
      if (success) {
        router.dismissAll();
        router.replace('/(tabs)/family');
      }
    } catch (err) {
      console.error('Failed to delete member:', err);
    } finally {
      setIsSaving(false);
    }
  }, [deleteMember, router]);

  const handleManageCalendars = useCallback(() => {
    router.push(`/family-member/${id}/calendars`);
  }, [router, id]);

  const handleEditNotes = useCallback(() => {
    router.push(`/family-member/${id}/notes`);
  }, [router, id]);

  const handlePhotoChanged = useCallback(async (uri: string | null) => {
    try {
      await updateMember({ profilePicture: uri });
    } catch (err) {
      console.error('Failed to update profile picture:', err);
    }
  }, [updateMember]);

  const handleColorChanged = useCallback(async (color: string) => {
    try {
      await updateMember({ color });
    } catch (err) {
      console.error('Failed to update member color:', err);
    }
  }, [updateMember]);

  const initialValues = useMemo(() => ({
    name: member?.name || '',
    dateOfBirth: member?.dateOfBirth || '',
    notes: member?.notes ? member.notes.join('\n') : '',
  }), [member]);

  if (isLoading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['top']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={palette.primary} />
        </View>
      </SafeAreaView>
    );
  }

  if (error || !member) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['top']}>
        <View style={[styles.header, { borderBottomColor: palette.borderMuted }]}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={palette.primary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: palette.text, fontFamily: getHeadingFont() }]}>
            Edit Member
          </Text>
          <View style={styles.headerSpacer} />
        </View>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={48} color={palette.danger} />
          <Text style={[styles.errorText, { color: palette.text, fontFamily: getBodyFont() }]}>
            {error || 'Family member not found'}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  const calendarLabel = linkedCalendarIds.length === 0
    ? 'No calendars linked'
    : `${linkedCalendarIds.length} calendar${linkedCalendarIds.length === 1 ? '' : 's'} linked`;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['top']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: palette.borderMuted }]}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={palette.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: palette.text, fontFamily: getHeadingFont() }]}>
          Edit Member
        </Text>
        <View style={styles.headerSpacer} />
      </View>

      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        {/* Photo Editor */}
        <Animated.View entering={FadeIn.duration(300)} style={styles.avatarSection}>
          <FamilyMemberPhotoEditor
            imageUri={member.profilePicture}
            name={member.name}
            color={member.color}
            accentColor={palette.primary}
            textColor={palette.text}
            backgroundColor={palette.cardBackground}
            onPhotoChanged={handlePhotoChanged}
          />
          <FamilyMemberColorPicker
            currentColor={member.color}
            onColorChanged={handleColorChanged}
            accentColor={palette.primary}
            textColor={palette.text}
            textMutedColor={palette.textMuted}
            backgroundColor={palette.background}
            cardBackgroundColor={palette.cardBackground}
          />
          {member.isMe && (
            <View style={[styles.badge, { backgroundColor: palette.primary }]}>
              <Text style={[styles.badgeText, { color: getContrastTextColor(palette.primary), fontFamily: getBodyFont() }]}>You</Text>
            </View>
          )}
        </Animated.View>

        {/* Manage Calendars Action */}
        <Animated.View entering={FadeIn.duration(300).delay(100)} style={styles.actionSection}>
          <TouchableOpacity
            style={[styles.actionCard, Shadows.sm, { backgroundColor: palette.cardBackground }]}
            onPress={handleManageCalendars}
            activeOpacity={0.7}
          >
            <View style={styles.actionRow}>
              <View style={styles.actionLeft}>
                <Ionicons name="calendar" size={20} color={palette.primary} />
                <View style={styles.actionInfo}>
                  <Text style={[styles.actionTitle, { color: palette.text, fontFamily: getBodyFont() }]}>
                    Filter Calendars
                  </Text>
                  <Text style={[styles.actionSubtitle, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                    {calendarLabel}
                  </Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={20} color={palette.textMuted} />
            </View>
          </TouchableOpacity>
        </Animated.View>

        {/* Form */}
        <FamilyMemberForm
          initialValues={initialValues}
          mode="edit"
          isLoading={isSaving}
          canDelete={!member.isMe}
          onSubmit={handleSave}
          onDelete={handleDelete}
          onEditNotes={handleEditNotes}
          notesCount={member.notes?.length ?? 0}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    padding: Spacing.xs,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  headerSpacer: {
    width: 32,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: Spacing.lg,
    padding: Spacing.xxl,
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
  },
  keyboardView: {
    flex: 1,
  },
  avatarSection: {
    alignItems: 'center',
    paddingVertical: Spacing.xxl,
    gap: Spacing.sm,
  },
  badge: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.xs,
    borderRadius: BorderRadius.md,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  actionSection: {
    paddingHorizontal: Spacing.lg,
    marginBottom: Spacing.sm,
  },
  actionCard: {
    borderRadius: BorderRadius.md,
    padding: Spacing.lg,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    flex: 1,
  },
  actionInfo: {
    flex: 1,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '500',
  },
  actionSubtitle: {
    fontSize: 12,
    marginTop: Spacing.xxs,
  },
});
